import { ChevronDown, PackageCheck, X } from 'lucide-react';
import { useState } from 'react';
import type { BackorderRow, BackorderStatus } from '@eombor/contracts';
import { backorderStatusLabel } from '@eombor/contracts';
import { Badge, Button, cn, type BadgeTone } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { BackorderGroup } from '../model/grouping.js';
import type { BackorderSelection } from '../model/selection.js';
import { PickBox } from './PickBox.js';

/**
 * GURUHLANGAN RO'YXAT — sarlavha va uning ostidagi arizalar.
 *
 * Jadval EMAS va bu ataylab: har guruh uchun alohida jadval chizilsa,
 * ustun sarlavhalari o'n marta takrorlanib, ro'yxatning o'zidan ko'p
 * joy egallardi. Bu yerda sarlavha bir marta — guruhning O'ZIDA, va
 * unda skladchiga kerak bo'lgan yagona son turadi: JAMI qancha.
 *
 * Guruh yopiladi: o'nta mahsulot bo'yicha o'ttizta ariza yig'ilganda
 * hammasi ochiq tursa, ro'yxat yana ekranga sig'may qolardi.
 */

const TONES: Record<BackorderStatus, BadgeTone> = {
  SUBMITTED: 'info',
  ACCEPTED: 'warn',
  FULFILLED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

export interface BackorderGroupsProps {
  groups: readonly BackorderGroup[];
  /**
   * Qatorda MAHSULOT nomi ko'rsatilsinmi.
   *
   * Mahsulot bo'yicha guruhlaganda nom allaqachon SARLAVHADA turadi va
   * uni har qatorda takrorlash bir xil so'zni o'n marta yozish bo'lardi.
   * To'plam bo'yicha guruhlaganda esa aksincha: har qator boshqa narsa
   * va nomsiz qatorlar bir-biridan farq qilmasdi.
   */
  showProduct: boolean;
  /** Belgilash — faqat «Kutilmoqda» bo'limida beriladi. */
  selection?: BackorderSelection;
  actionsFor: (row: BackorderRow) => {
    onAccept?: (row: BackorderRow) => void;
    onFulfil?: (row: BackorderRow) => void;
    onCancel?: (row: BackorderRow) => void;
  };
}

export const BackorderGroups = ({
  groups,
  actionsFor,
  showProduct,
  selection,
}: BackorderGroupsProps) => {
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (key: string): void =>
    setClosed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <ul className="flex flex-col gap-2">
      {groups.map((group) => {
        const open = !closed.has(group.key);
        return (
          <li
            key={group.key}
            className="overflow-hidden rounded-[var(--radius-lg)] border border-border bg-surface"
          >
            <button
              type="button"
              onClick={() => toggle(group.key)}
              aria-expanded={open}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-2.5 text-left',
                'bg-surface-2 transition-colors duration-[var(--motion-fast)] hover:bg-accent-soft',
                'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent',
              )}
            >
              <ChevronDown
                size={16}
                aria-hidden
                className={cn('shrink-0 text-text-muted transition-transform', !open && '-rotate-90')}
              />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-text">
                {group.title}
              </span>

              {/* JAMI — guruhning butun ma'nosi. Ta'minotga buyurtma
                  aynan shu son bilan beriladi. Birlik aralash bo'lsa
                  ko'rsatilmaydi: metr bilan donaning yig'indisi yolg'on. */}
              <span className="shrink-0 text-xs text-text-muted">
                {t('backorder.groupCount', { count: group.rows.length })}
              </span>
              <span className="tabular shrink-0 text-sm font-semibold text-text">
                {qtyText(group.totalQty)} {group.unit ?? ''}
              </span>
            </button>

            {open ? (
              <ul className="flex flex-col divide-y divide-border">
                {group.rows.map((row) => {
                  const actions = actionsFor(row);
                  return (
                    <li
                      key={row.id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5"
                    >
                      {selection === undefined ? null : (
                        <PickBox row={row} selection={selection} />
                      )}
                      <span className="font-mono text-xs text-text-muted">{row.docNo}</span>
                      <Badge tone={TONES[row.status]}>{backorderStatusLabel(row.status)}</Badge>
                      <span className="min-w-0 flex-1 truncate text-sm text-text">
                        {row.requesterName}
                      </span>
                      {showProduct ? (
                        <span className="min-w-0 flex-1 truncate text-sm text-text-muted">
                          {row.nomenclatureName ?? row.freeText}
                        </span>
                      ) : null}
                      <span className="tabular shrink-0 text-sm font-medium text-text">
                        {qtyText(row.requestedQty)} {row.unit ?? ''}
                      </span>

                      <span className="flex shrink-0 items-center gap-1.5">
                        {actions.onCancel === undefined ? null : (
                          <Button variant="ghost" size="sm" onClick={() => actions.onCancel?.(row)}>
                            <X size={14} aria-hidden />
                            {t('backorder.withdraw')}
                          </Button>
                        )}
                        {actions.onAccept === undefined ? null : (
                          <Button size="sm" onClick={() => actions.onAccept?.(row)}>
                            {t('backorder.decide')}
                          </Button>
                        )}
                        {actions.onFulfil === undefined ? null : (
                          <Button size="sm" onClick={() => actions.onFulfil?.(row)}>
                            <PackageCheck size={14} aria-hidden />
                            {t('backorder.fulfil')}
                          </Button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
